import { useCallback } from 'react';
import { useUpdateAccount } from '../../auth/hooks/useUpdateAccount';
import { Account } from '../../auth';
import {
  DEFAULT_THEME_PREFERENCE,
  readStoredPreference,
  type ThemePreference,
} from './useTheme';

const isPreference = (value: unknown): value is ThemePreference =>
  value === 'system' || value === 'light' || value === 'dark';

/**
 * Reconciles the local appearance preference with the account on sign-in,
 * mirroring `useSetDefaultPreferredLanguage`.
 *
 * Direction matters. The account is the cross-device source of truth, so if
 * it already carries a theme it wins and is pushed back into local storage.
 * Only when the account has none does the local choice get promoted, which
 * preserves a preference set before the user signed in.
 *
 * Returns the preference the caller should apply, so the caller can update
 * the store without waiting on the mutation.
 */
export const useSetDefaultTheme = () => {
  const { mutateAsync } = useUpdateAccount();

  return useCallback(
    async (account: Account): Promise<ThemePreference> => {
      const stored = account.preferences?.theme;

      if (isPreference(stored)) return stored;

      const local = readStoredPreference();

      // Nothing to persist: the account has no theme and neither has the user.
      if (!local) return DEFAULT_THEME_PREFERENCE;

      // `Account.id` is optional in the generated type. Without one there is
      // nothing to update, and sending `{ id: undefined }` would be a silent
      // no-op against the API rather than an error.
      if (!account.id) return local;

      await mutateAsync({
        input: {
          id: account.id,
          preferences: {
            ...account.preferences,
            theme: local,
          },
        },
      });

      return local;
    },
    [mutateAsync],
  );
};
